import { useEffect, useState } from 'react';
import { supabase } from '../../../lib/supabase';
import { getErrorMessage } from '../../../lib/errors';
import { useAuthStore } from '../../../stores/authStore';
import { CardNB, ButtonNB, IconNB } from '../../../components/ui-nb';
import { error as logError } from '../../../lib/logger';

interface EquipmentType {
  id: string;
  name: string;
  created_at: string;
}

export default function EquipmentTypeList() {
  const [types, setTypes] = useState<EquipmentType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [newTypeName, setNewTypeName] = useState('');
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');

  const organizationId = useAuthStore(s => s.user?.organizationId);

  useEffect(() => {
    if (!organizationId) return;
    
    async function fetchTypes() {
      try {
        const { data, error: fetchError } = await supabase
          .from('equipment_types')
          .select('*')
          .eq('organization_id', organizationId)
          .order('name');
        
        if (fetchError) throw fetchError;
        setTypes(data ?? []);
      } catch (err) {
        logError('Error fetching equipment types', { error: getErrorMessage(err) });
        setError(getErrorMessage(err) || 'Failed to load equipment types');
      } finally {
        setLoading(false);
      }
    }
    
    fetchTypes();
  }, [organizationId]);

  const handleAddType = async () => {
    if (!newTypeName.trim() || !organizationId) return;
    
    setSaving(true);
    try {
      const { error: insertError } = await supabase
        .from('equipment_types')
        .insert({
          name: newTypeName.trim(),
          organization_id: organizationId,
        });
      
      if (insertError) throw insertError;
      
      const { data, error: refreshError } = await supabase
        .from('equipment_types')
        .select('*')
        .eq('organization_id', organizationId)
        .order('name');
      
      if (refreshError) throw refreshError;
      setTypes(data ?? []);
      setNewTypeName('');
      setIsAdding(false);
    } catch (err) {
      logError('Error adding equipment type', { error: getErrorMessage(err) });
        setError(getErrorMessage(err) || 'Failed to add equipment type');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteType = async (id: string) => {
    try {
      const { error: deleteError } = await supabase
        .from('equipment_types')
        .delete()
        .eq('id', id);
      
      if (deleteError) throw deleteError;
      
      setTypes(prev => prev.filter(t => t.id !== id));
      setConfirmDeleteId(null);
      setDeletingId(null);
    } catch (err) {
      logError('Error deleting equipment type', { error: getErrorMessage(err) });
        setError(getErrorMessage(err) || 'Failed to delete equipment type');
    }
  };

  const handleStartEdit = (type: EquipmentType) => {
    setEditingId(type.id);
    setEditName(type.name);
    setError(null);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditName('');
  };

  const handleSaveEdit = async () => {
    if (!editName.trim() || !editingId) return;
    
    setSaving(true);
    try {
      const { error: updateError } = await supabase
        .from('equipment_types')
        .update({ name: editName.trim() })
        .eq('id', editingId);
      
      if (updateError) throw updateError;
      
      setTypes(prev => prev.map(t => t.id === editingId ? { ...t, name: editName.trim() } : t));
      setEditingId(null);
      setEditName('');
    } catch (err) {
      logError('Error updating equipment type', { error: getErrorMessage(err) });
        setError(getErrorMessage(err) || 'Failed to update equipment type');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <span className="text-nb-text-muted">Loading...</span>
      </div>
    );
  }

  return (
    <>
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-2xl font-bold text-nb-black">
          Equipment Types
        </h1>
        {!isAdding && (
          <ButtonNB variant="primary" size="sm" onClick={() => setIsAdding(true)}>
            <IconNB name="plus" size={16} />
            Add Type
          </ButtonNB>
        )}
      </div>

      {error && (
        <div className="mb-4 p-3 rounded bg-nb-red/10 text-nb-red text-sm flex items-center justify-between">
          {error}
          <button 
            onClick={() => setError(null)} 
            className="bg-none border-none text-inherit cursor-pointer"
          >
            <IconNB name="close" size={16} />
          </button>
        </div>
      )}

      {/* Add Type Form */}
      {isAdding && (
        <CardNB className="p-4 mb-6">
          <div className="flex gap-3 items-center">
            <input
              type="text"
              value={newTypeName}
              onChange={e => setNewTypeName(e.target.value)}
              placeholder="Type name (e.g., Smartphone)"
              autoFocus
              onKeyDown={e => {
                if (e.key === 'Enter') handleAddType();
                if (e.key === 'Escape') { setIsAdding(false); setNewTypeName(''); }
              }}
              className="flex-1 bg-nb-white border-3 border-nb-black px-3 py-2 text-sm font-nb rounded-[2px] focus:border-nb-yellow focus:outline-none"
            />
            <ButtonNB 
              variant="primary" 
              size="sm" 
              onClick={handleAddType}
              loading={saving}
              disabled={!newTypeName.trim()}
            >
              Save
            </ButtonNB>
            <ButtonNB 
              variant="secondary" 
              size="sm" 
              onClick={() => { setIsAdding(false); setNewTypeName(''); }}
            >
              Cancel
            </ButtonNB>
          </div>
        </CardNB>
      )}

      {/* Equipment Types List */}
      {types.length === 0 && !isAdding ? (
        <CardNB className="p-12 text-center">
          <IconNB name="equipment" size={48} className="text-nb-text-muted mb-4" />
          <p className="text-nb-text-muted text-sm">
            No equipment types configured. Add your first type to get started.
          </p>
        </CardNB>
      ) : (
        <div className="flex flex-col gap-3">
          {types.map(type => (
            <CardNB key={type.id} className="p-4">
              {editingId === type.id ? (
                <div className="flex gap-3 items-center">
                  <input
                    type="text"
                    value={editName}
                    onChange={e => setEditName(e.target.value)}
                    autoFocus
                    onKeyDown={e => {
                      if (e.key === 'Enter') handleSaveEdit();
                      if (e.key === 'Escape') handleCancelEdit();
                    }}
                    className="flex-1 bg-nb-white border-3 border-nb-black px-3 py-2 text-sm font-nb rounded-[2px] focus:border-nb-yellow focus:outline-none"
                  />
                  <ButtonNB 
                    variant="primary" 
                    size="sm" 
                    onClick={handleSaveEdit}
                    loading={saving}
                    disabled={!editName.trim()}
                  >
                    Save
                  </ButtonNB>
                  <ButtonNB 
                    variant="secondary" 
                    size="sm"
                    onClick={handleCancelEdit}
                    disabled={saving}
                  >
                    Cancel
                  </ButtonNB>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <IconNB name="equipment" size={20} className="text-nb-black" />
                    <span className="text-nb-black font-nb font-medium">{type.name}</span>
                  </div>
                  
                  {confirmDeleteId === type.id ? (
                    <div className="flex gap-2 items-center">
                      <span className="text-nb-text-muted text-sm">Delete?</span>
                      <ButtonNB 
                        variant="primary" 
                        size="sm"
                        onClick={() => handleDeleteType(type.id)}
                        className="bg-nb-red border-nb-red"
                      >
                        Confirm
                      </ButtonNB>
                      <ButtonNB 
                        variant="secondary" 
                        size="sm"
                        onClick={() => { setConfirmDeleteId(null); setDeletingId(null); }}
                      >
                        Cancel
                      </ButtonNB>
                    </div>
                  ) : (
                    <div className="flex gap-1 items-center">
                      <ButtonNB 
                        variant="secondary" 
                        size="sm"
                        onClick={() => handleStartEdit(type)}
                        disabled={saving}
                        className="border-nb-black/50"
                      >
                        Rename
                      </ButtonNB>
                      <ButtonNB 
                        variant="secondary" 
                        size="sm"
                        onClick={() => { setDeletingId(type.id); setConfirmDeleteId(type.id); }}
                        disabled={saving}
                        className="border-nb-black/50"
                      >
                        <IconNB name="trash" size={16} className="text-nb-red" />
                      </ButtonNB>
                    </div>
                  )}
                </div>
              )}
            </CardNB>
          ))}
        </div>
      )}
    </>
  );
}